/**
 * AuthModal — Unified authentication modal (Single SOT)
 * 
 * Replaces FavoriteAuthModal, GuestExitPreventionModal, and PaywallModal.
 * 
 * Variants:
 * - default:          Generic sign-in prompt
 * - favorite:         "Save to your favorites" prompt
 * - exit-prevention:  "Save your conversation?" with Leave/Cancel actions
 * - paywall:          Guest → auth, Authenticated → PlanSelector
 *                     (only when bypass is OFF and content is truly gated)
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { AuthFormCard } from '@/components/auth/AuthFormCard';
import { SignupStatsPanel } from '@/components/auth/SignupStatsPanel';
import { PlanSelector } from '@/components/shared/PlanSelector';
import { useBranding } from '@/hooks/useBranding';
import { LComponents } from '@/components/i18n';
import { isReturningVisitor } from '@/utils/authRedirect';
import { storePaywallReturnUrl } from '@/lib/paywallReturn';
import { ROUTES } from '@/constants';
import { DIALOGS, LAYOUTS } from '@/design-system';
import type { MembershipPlanWithGateways } from '@/hooks/useMembershipPlans';

type AuthModalVariant = 'default' | 'favorite' | 'exit-prevention' | 'paywall';

interface AuthModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: AuthModalVariant;
  /** exit-prevention: chat session id passed to AuthFormCard */
  sessionId?: string | null;
  /** exit-prevention: callback when user chooses to leave */
  onLeave?: () => void;
  /** paywall: publication location id for plan filtering */
  contentLocationId?: number | null;
  /** Whether bypass_content_gating kill switch is ON — suppresses PlanSelector */
  isBypassed?: boolean;
}

export function AuthModal({
  open,
  onOpenChange,
  variant = 'default',
  sessionId,
  onLeave,
  contentLocationId,
  isBypassed = false,
}: AuthModalProps) {
  const { user } = useAuth();
  const { brand } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();

  const isAuthenticated = !!user;
  const defaultTab = isReturningVisitor() ? 'login' : 'signup';

  const handleAuthSuccess = (): void => {
    if (variant === 'paywall') {
      // If bypass is ON, close modal immediately after auth — no paywall needed
      if (isBypassed) {
        onOpenChange(false);
        return;
      }
      // Stay open — re-render switches to PlanSelector
      return;
    }
    onOpenChange(false);
  };

  const handleCancel = (): void => {
    onOpenChange(false);
  };

  const handleSelectPlan = (plan: MembershipPlanWithGateways, gatewayAccountId?: string): void => {
    storePaywallReturnUrl(location.pathname);
    const params = new URLSearchParams({ planId: plan.id });
    if (gatewayAccountId) params.set('gatewayId', gatewayAccountId);
    navigate(`${ROUTES.CHECKOUT}?${params.toString()}`);
    onOpenChange(false);
  };

  // --- Variant-specific header ---
  const renderHeader = (): React.ReactNode => {
    if (variant === 'paywall' && isAuthenticated && !isBypassed) {
      return null;
    }

    return (
      <div className="flex justify-start py-2">
        <img
          src={brand.logoSrc}
          alt={brand.logoAlt}
          className="h-10 w-auto object-contain"
          draggable={false}
        />
      </div>
    );
  };

  // --- Variant-specific footer ---
  const renderFooter = (): React.ReactNode => {
    if (variant === 'paywall') return null;

    if (variant === 'exit-prevention') {
      return (
        <DialogFooter className="flex-col sm:flex-row gap-2 pt-2 border-t justify-center">
          <Button variant="outline" onClick={handleCancel}>
            <LComponents id="guest_exit_modal.cancel" defaultValue="Continue chatting" />
          </Button>
          <Button
            variant="secondary"
            onClick={onLeave}
            className="bg-muted text-muted-foreground hover:bg-muted/80 gap-2"
          >
            <LogOut className="h-4 w-4" />
            <LComponents id="guest_exit_modal.leave_anyway" defaultValue="Leave anyway" />
          </Button>
        </DialogFooter>
      );
    }

    // default + favorite
    return (
      <DialogFooter className="justify-center">
        <Button variant="outline" onClick={handleCancel}>
          <LComponents id="auth_modal.cancel" defaultValue="Cancel" />
        </Button>
      </DialogFooter>
    );
  };

  // --- Body content ---
  const renderBody = (): React.ReactNode => {
    // Paywall variant + authenticated user:
    // Only show PlanSelector when bypass is OFF.
    // When bypass is ON, close modal (user already has access).
    if (variant === 'paywall' && isAuthenticated) {
      if (isBypassed) {
        // Bypass is ON — close the modal, content should expand
        // Use setTimeout to avoid state update during render
        setTimeout(() => onOpenChange(false), 0);
        return null;
      }
      return <PlanSelector onSelectPlan={handleSelectPlan} contentLocationId={contentLocationId} />;
    }

    return (
      <div className={LAYOUTS.authGrid}>
        <div className="hidden md:block">
          <SignupStatsPanel />
        </div>
        <AuthFormCard
          initialTab={defaultTab as 'login' | 'signup'}
          sessionId={variant === 'exit-prevention' ? sessionId : undefined}
          onSuccess={handleAuthSuccess}
          className="shadow-none border-0 p-0"
        />
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={DIALOGS.xxlargeScroll}>
        {/* Screen-reader title (Radix requires one); visually hidden */}
        <DialogTitle className="sr-only">{brand.displayName}</DialogTitle>
        {renderHeader()}
        {renderBody()}
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
}
